import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiCalendar } from "react-icons/fi";
import { FormGroup, FormControl, FormLabel, Row, Col } from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import es from "date-fns/locale/es";
import { toastr } from "react-redux-toastr";

import { transactionsService } from "services";
import { errorFormating } from "utils/utils";
import SubmitButton from "components/SubmitButton";
import DurationInput from "components/TimesBalance/DurationInput";
import {
	balanceSpanClass,
	industrialMinutesToHours,
	isValidDurationMinutes,
	minutesToDisplay,
	transactionValueToMinutes,
} from "utils/timeBalance";

registerLocale("es", es);

function entryEffectMinutes(minutes, operation) {
	if (!minutes || minutes <= 0) return 0;
	return operation === "add" ? minutes : -minutes;
}

const ManualEntryModal = ({
	transaction,
	employeeId,
	accountId,
	week,
	weekStart,
	weekEnd,
	balanceMinutes = 0,
	cumulativeMinutes = 0,
	hideWeeklyBalance = false,
	onSaved,
	handleClose,
}) => {
	const { t } = useTranslation();
	const isEdit = !!transaction;

	const initialMinutes = transaction
		? Math.abs(transactionValueToMinutes(transaction.value))
		: 0;
	const initialOperation =
		transaction && (transaction.value || 0) < 0 ? "subtract" : "add";

	const defaultDay =
		transaction?.day ||
		(weekEnd ? moment(weekEnd).format("YYYY-MM-DD") : "");

	const [day, setDay] = useState(defaultDay);
	const [durationMinutes, setDurationMinutes] = useState(initialMinutes);
	const [operation, setOperation] = useState(initialOperation);
	const [observations, setObservations] = useState(transaction?.observations || "");
	const [isLoading, setIsLoading] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [errors, setErrors] = useState({});

	const handleDateChange = (date) => {
		setDay(date ? moment(date).format("YYYY-MM-DD") : "");
	};

	const existingEntryMinutes = isEdit
		? transactionValueToMinutes(transaction.value)
		: 0;

	const entryDelta = useMemo(() => {
		const nextEffect = entryEffectMinutes(durationMinutes, operation);
		return nextEffect - existingEntryMinutes;
	}, [durationMinutes, operation, existingEntryMinutes]);

	const previewBalanceMinutes = balanceMinutes + entryDelta;
	const previewCumulativeMinutes = cumulativeMinutes + entryDelta;

	const validate = () => {
		const nextErrors = {};
		if (!day) nextErrors.day = t("times.balance.manualEntry.errors.dateRequired");
		if (!isValidDurationMinutes(durationMinutes)) {
			nextErrors.duration = t("times.balance.manualEntry.errors.valueRequired");
		}
		setErrors(nextErrors);
		return Object.keys(nextErrors).length === 0;
	};

	const handleDelete = async () => {
		if (!window.confirm(t("times.balance.manualEntry.deleteConfirmation"))) {
			return;
		}

		setIsDeleting(true);
		const response = await transactionsService.remove(transaction.id);
		setIsDeleting(false);

		if (response.ok) {
			toastr.success(t("common.success"), t("common.changesSaved"));
			onSaved();
			handleClose();
		} else {
			setErrors(errorFormating(response));
		}
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setSubmitted(true);
		if (!validate()) return;

		const mins = durationMinutes;

		setIsLoading(true);
		const payload = {
			employee_id: employeeId,
			account_id: accountId,
			day,
			value: industrialMinutesToHours(mins, operation),
			observations,
		};

		let response;
		if (isEdit) {
			response = await transactionsService.update({ ...payload, id: transaction.id });
		} else {
			response = await transactionsService.add(payload);
		}

		setIsLoading(false);
		if (response.ok) {
			toastr.success(t("common.success"), t("common.changesSaved"));
			onSaved();
			handleClose();
		} else {
			setErrors(errorFormating(response));
		}
	};

	const checkValidField = (name) =>
		submitted && errors[name] !== undefined && errors[name] !== "";

	const minDate = weekStart?.toDate?.() ?? weekStart;
	const maxDate = weekEnd?.toDate?.() ?? weekEnd;

	const balanceRows = [
		{
			key: "balanceAfterManual",
			label: t("times.balance.balanceAfterManual"),
			minutes: previewBalanceMinutes,
		},
		{
			key: "acumuladoAfterManual",
			label: t("times.balance.accumulatedAfterManual"),
			minutes: previewCumulativeMinutes,
		},
	].filter((row) => !(hideWeeklyBalance && row.key === "balanceAfterManual"));

	return (
		<form className="modal-transaction tb-manual-entry-modal">
			<div className="modal-body">
				{week && (
					<div className="tb-manual-entry-modal__week">
						<div className="tb-week-header">
							<div className="tb-week-label">
								{t("times.balance.week", { weekNumber: week.weekNumber })}
							</div>
							<div className="tb-week-range">
								{week.start.format("D MMM").toUpperCase()} -{" "}
								{week.end.format("D MMM").toUpperCase()}
							</div>
						</div>
					</div>
				)}

				<div className="tb-manual-entry-modal__balances">
					{balanceRows.map(({ key, label, minutes }) => (
						<div key={key} className="tb-manual-entry-modal__balance-row">
							<span className="tb-manual-entry-modal__balance-label">
								{label}
							</span>
							<span className={balanceSpanClass(key)}>
								{minutesToDisplay(minutes)}
							</span>
						</div>
					))}
				</div>

				<FormGroup
					controlId="day"
					className={`form-group form-date ${(!day || day === "") && "empty"} ${
						checkValidField("day") ? "is-invalid" : ""
					}`}
				>
					<FormLabel>
						{t("transactions.form.date")}{" "}
						<span className="label-required">*</span>
					</FormLabel>
					<DatePicker
						selected={day ? moment(day).toDate() : null}
						onChange={handleDateChange}
						className="form-control"
						dateFormat="dd/MM/yyyy"
						locale="es"
						placeholderText="dd/mm/aaaa"
						minDate={minDate}
						maxDate={maxDate}
					/>
					<FiCalendar />
					<FormControl.Feedback type="invalid">{errors.day}</FormControl.Feedback>
				</FormGroup>

				<Row>
					<Col>
						<FormGroup
							className={`form-group${checkValidField("duration") ? " is-invalid" : ""}`}
							controlId="duration"
						>
							<FormLabel>
								{t("times.balance.manualEntry.valueLabel")}{" "}
								<span className="label-required">*</span>
							</FormLabel>
							<DurationInput
								initialMinutes={initialMinutes}
								minutes={durationMinutes}
								onChange={setDurationMinutes}
								isInvalid={checkValidField("duration")}
							/>
							<FormControl.Feedback type="invalid">
								{errors.duration}
							</FormControl.Feedback>
						</FormGroup>
					</Col>
				</Row>

				<FormGroup className="form-group tb-manual-entry-modal__operation">
					<FormLabel>{t("times.balance.manualEntry.operationLabel")}</FormLabel>
					<div className="tb-manual-entry-modal__radios">
						<label className="tb-manual-entry-modal__radio">
							<input
								type="radio"
								name="operation"
								value="add"
								checked={operation === "add"}
								onChange={() => setOperation("add")}
							/>
							<span>{t("times.balance.manualEntry.addTime")}</span>
						</label>
						<label className="tb-manual-entry-modal__radio">
							<input
								type="radio"
								name="operation"
								value="subtract"
								checked={operation === "subtract"}
								onChange={() => setOperation("subtract")}
							/>
							<span>{t("times.balance.manualEntry.subtractTime")}</span>
						</label>
					</div>
				</FormGroup>

				<FormGroup className="form-group" controlId="observations">
					<FormLabel>{t("transactions.form.observations")}</FormLabel>
					<FormControl
						as="textarea"
						value={observations}
						onChange={(event) => setObservations(event.target.value)}
						placeholder={t("transactions.form.observations")}
					/>
				</FormGroup>

			</div>

			<div className="modal-footer tb-manual-entry-modal__footer">
				{isEdit && (
					<button
						type="button"
						onClick={handleDelete}
						className="btn btn-outline-danger tb-manual-entry-modal__delete-btn"
						disabled={isLoading || isDeleting}
					>
						{isDeleting ? t("common.deleting") : t("common.delete")}
					</button>
				)}
				<button
					type="button"
					onClick={handleClose}
					className="btn btn-outline-primary"
					disabled={isLoading || isDeleting}
				>
					{t("common.cancel")}
				</button>
				<SubmitButton
					type="button"
					isLoading={isLoading}
					text={t("common.save")}
					loadingText={t("common.saving")}
					onClick={handleSubmit}
					disabled={isDeleting}
				/>
			</div>
		</form>
	);
};

export default ManualEntryModal;
